import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Route,
  RouterProvider,
  Routes,
} from 'react-router-dom';
import GroupBillingRoute from './components/GroupBillingRoute';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import HotelShell from './design-v2/hotel/shell/HotelShell';
import EventsGate from './features/events/EventsGate';
import EventsLayout from './features/events/EventsLayout';
import { eventsChildRoutes } from './features/events/routes';
import AppLayout from './layout/AppLayout';
import AuthLayout from './layout/AuthLayout';
import KnowledgeBaseArtifactViewer from './pages/AdminPages/KnowledgeBaseArtifactViewer';
import PlatformAdmin from './pages/AdminPages/PlatformAdmin';
import ForgotPasswordPage from './pages/AuthPages/ForgotPasswordPage';
import ResetPasswordPage from './pages/AuthPages/ResetPasswordPage';
import SignIn from './pages/AuthPages/SignIn';
import BillingPage from './pages/Billing/BillingPage';
import PlansPage from './pages/Billing/PlansPage';
import Blank from './pages/Blank';
import Dashboard from './pages/Dashboard/Dashboard';
import FormElements from './pages/Forms/FormElements';
import GettingStartedPage from './pages/GettingStarted/GettingStartedPage';
import GroupOnboardingPage from './pages/GroupOnboarding/GroupOnboardingPage';
import IntegrationsPage from './pages/Integrations/IntegrationsPage';
import PosCallbackError from './pages/Integrations/PosCallbackError';
import PosCallbackSuccess from './pages/Integrations/PosCallbackSuccess';
import PosLinkStore from './pages/Integrations/PosLinkStore';
import KnowledgeQuestions from './pages/KnowledgeQuestions/KnowledgeQuestions';
import OpenTable from './pages/OpenTable/OpenTable';
import AgentSettings from './pages/OtherPage/AgentSettings';
import NotFound from './pages/OtherPage/NotFound';
import ProfilePage from './pages/Profile/ProfilePage';
import ActivationPage from './pages/Public/ActivationPage';
import OptInPage from './pages/Public/OptInPage';
import OptInSuccess from './pages/Public/OptInSuccess';
import Privacy from './pages/Public/Privacy';
import StoreOnboardingLinkPage from './pages/Public/StoreOnboardingLinkPage';
import StoreOnboardingPage from './pages/Public/StoreOnboardingPage';
import Terms from './pages/Public/Terms';
import ProspectDetailPage from './pages/Sales/ProspectDetailPage';
import ProspectsPage from './pages/Sales/ProspectsPage';
import SalesSettingsPage from './pages/Sales/SalesSettingsPage';
import SmsInbox from './pages/SmsInbox/SmsInbox';

import SpecialCatering from './pages/SpecialCatering/SpecialCatering';
import SpecialEvents from './pages/SpecialEvents/SpecialEvents';
import StoreInfoPage from './pages/StoreInfo/v2/StoreInfoPage';
import CallsTable from './pages/Tables/CallsTable';
import CustomerTable from './pages/Tables/CustomerTable';
import InventoryTable from './pages/Tables/InventoryTable';
import OrderTable from './pages/Tables/OrderTable';
import RefundsTable from './pages/Tables/RefundsTable';
import ReservationTables from './pages/Tables/ReservationsTable';
import TestingPage from './pages/Testing/TestingPage';
import Alerts from './pages/UiElements/Alerts';
import Avatars from './pages/UiElements/Avatars';
import Badges from './pages/UiElements/Badges';
import Buttons from './pages/UiElements/Buttons';
import Images from './pages/UiElements/Images';
import Videos from './pages/UiElements/Videos';
import Voicemail from './pages/Voicemail/Voicemail';

const GuestPortal = lazy(() => import('./pages/Guest/GuestPortal'));
const HotelApp = lazy(() => import('./design-v2/hotel/HotelApp'));
// Development-only design preview with sample data; not part of production builds.
const DashboardPreview =
  process.env.NODE_ENV === 'development'
    ? lazy(() => import('./pages/Dashboard/preview/DashboardPreview'))
    : null;

/** Every staff route. Exported for the shell routing tests. */
export function AppRoutes() {
  return (
    <AuthProvider>
      <Routes>
        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Public pages */}
          <Route path="/opt-in/:storeUuid" element={<OptInPage />} />
          <Route path="/opt-in/:storeUuid/success" element={<OptInSuccess />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/store-onboarding" element={<StoreOnboardingPage />} />
          <Route
            path="/store-onboarding/:token"
            element={<StoreOnboardingLinkPage />}
          />
          <Route path="/activate" element={<ActivationPage />} />
          <Route path="/activate/:token" element={<ActivationPage />} />
        </Route>

        {/* Protected restaurant routes (RestaurantShell). A hotel-only user
            is sent to /hotel before any restaurant chrome renders. */}
        <Route element={<AppLayout />}>
          <Route
            path="/"
            element={<ProtectedRoute element={<Dashboard />} />}
          />
          {DashboardPreview && (
            <Route
              path="/design-preview/dashboard"
              element={
                <ProtectedRoute
                  element={
                    <Suspense fallback={null}>
                      <DashboardPreview />
                    </Suspense>
                  }
                />
              }
            />
          )}
          <Route
            path="/profile"
            element={<ProtectedRoute element={<ProfilePage />} />}
          />
          <Route
            path="/billing"
            element={<ProtectedRoute element={<BillingPage />} />}
          />
          <Route
            path="/billing/plans"
            element={<ProtectedRoute element={<PlansPage />} />}
          />
          <Route
            path="/onboarding"
            element={<ProtectedRoute element={<GroupOnboardingPage />} />}
          />
          <Route
            path="/billing/group"
            element={
              <GroupBillingRoute element={<BillingPage scope="group" />} />
            }
          />
          <Route
            path="/billing/group/:groupId"
            element={
              <GroupBillingRoute element={<BillingPage scope="group" />} />
            }
          />
          <Route
            path="/billing/group/:groupId/plans"
            element={
              <GroupBillingRoute element={<PlansPage scope="group" />} />
            }
          />
          <Route
            path="/blank"
            element={<ProtectedRoute element={<Blank />} />}
          />
          <Route
            path="/calls"
            element={<ProtectedRoute element={<CallsTable />} />}
          />
          <Route
            path="/voicemail"
            element={<ProtectedRoute element={<Voicemail />} />}
          />
          <Route
            path="/voicemail/:id"
            element={<ProtectedRoute element={<Voicemail />} />}
          />
          <Route
            path="/knowledge-questions"
            element={<ProtectedRoute element={<KnowledgeQuestions />} />}
          />
          <Route
            path="/knowledge-questions/:id"
            element={<ProtectedRoute element={<KnowledgeQuestions />} />}
          />
          <Route
            path="/form-elements"
            element={<ProtectedRoute element={<FormElements />} />}
          />
          <Route
            path="/orders"
            element={<ProtectedRoute element={<OrderTable />} />}
          />
          <Route
            path="/customer-tables"
            element={<ProtectedRoute element={<CustomerTable />} />}
          />
          <Route
            path="/menu-availability"
            element={<ProtectedRoute element={<InventoryTable />} />}
          />
          <Route
            path="/inventory-tables"
            element={<Navigate to="/menu-availability" replace />}
          />
          <Route
            path="/agent-settings"
            element={<ProtectedRoute element={<AgentSettings />} />}
          />
          <Route
            path="/alerts"
            element={<ProtectedRoute element={<Alerts />} />}
          />
          <Route
            path="/avatars"
            element={<ProtectedRoute element={<Avatars />} />}
          />
          <Route
            path="/badges"
            element={<ProtectedRoute element={<Badges />} />}
          />
          <Route
            path="/buttons"
            element={<ProtectedRoute element={<Buttons />} />}
          />
          <Route
            path="/images"
            element={<ProtectedRoute element={<Images />} />}
          />
          <Route
            path="/videos"
            element={<ProtectedRoute element={<Videos />} />}
          />
          <Route
            path="/store-info/*"
            element={<ProtectedRoute element={<StoreInfoPage />} />}
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute
                element={<Navigate to="/store-info/notifications" replace />}
                allowedRoles={['Owner', 'Manager']}
              />
            }
          />
          <Route
            path="/reservations"
            element={<ProtectedRoute element={<ReservationTables />} />}
          />
          <Route
            path="/reservations/*"
            element={<ProtectedRoute element={<ReservationTables />} />}
          />
          <Route
            path="/special-events"
            element={<ProtectedRoute element={<SpecialEvents />} />}
          />
          <Route
            path="/special-events/:id"
            element={<ProtectedRoute element={<SpecialEvents />} />}
          />
          <Route
            path="/events"
            element={
              <ProtectedRoute
                element={
                  <EventsGate>
                    <EventsLayout />
                  </EventsGate>
                }
              />
            }
          >
            {eventsChildRoutes()}
          </Route>
          <Route
            path="/catering"
            element={<ProtectedRoute element={<SpecialCatering />} />}
          />
          <Route
            path="/catering/*"
            element={<ProtectedRoute element={<SpecialCatering />} />}
          />
          <Route
            path="/refunds"
            element={<ProtectedRoute element={<RefundsTable />} />}
          />
          <Route
            path="/getting-started"
            element={<ProtectedRoute element={<GettingStartedPage />} />}
          />
          {/* Test and Go live are steps of Getting started now; old links land on them. */}
          <Route
            path="/getting-started/test"
            element={<Navigate to="/getting-started?step=test" replace />}
          />
          <Route
            path="/getting-started/go-live"
            element={<Navigate to="/getting-started?step=go_live" replace />}
          />
          <Route
            path="/sms"
            element={<ProtectedRoute element={<SmsInbox />} />}
          />
          <Route
            path="/sms/:id"
            element={<ProtectedRoute element={<SmsInbox />} />}
          />
          <Route
            path="/integrations"
            element={<ProtectedRoute element={<IntegrationsPage />} />}
          />
          <Route
            path="/integrations/pos/success"
            element={<ProtectedRoute element={<PosCallbackSuccess />} />}
          />
          <Route
            path="/integrations/pos/link-store"
            element={<ProtectedRoute element={<PosLinkStore />} />}
          />
          <Route
            path="/integrations/pos/error"
            element={<ProtectedRoute element={<PosCallbackError />} />}
          />
          <Route
            path="/add-staff"
            element={<Navigate to="/store-info/team" replace />}
          />
          <Route
            path="/testing"
            element={
              <ProtectedRoute
                element={<TestingPage />}
                platformAdminOnly={true}
              />
            }
          />
          <Route
            path="/platform-admin"
            element={
              <ProtectedRoute
                element={<PlatformAdmin />}
                platformAdminOnly={true}
              />
            }
          />
          <Route
            path="/platform-admin/*"
            element={
              <ProtectedRoute
                element={<PlatformAdmin />}
                platformAdminOnly={true}
              />
            }
          />
          <Route
            path="/sales/prospects"
            element={
              <ProtectedRoute
                element={<ProspectsPage />}
                platformAdminOnly={true}
              />
            }
          />
          <Route
            path="/sales/settings"
            element={
              <ProtectedRoute
                element={<SalesSettingsPage />}
                platformAdminOnly={true}
              />
            }
          />
          <Route
            path="/sales/prospects/:prospectId"
            element={
              <ProtectedRoute
                element={<ProspectDetailPage />}
                platformAdminOnly={true}
              />
            }
          />
          <Route
            path="/artifacts/:artifactId"
            element={
              <ProtectedRoute
                element={<KnowledgeBaseArtifactViewer />}
                platformAdminOnly={true}
              />
            }
          />
          <Route
            path="/opentable/oauth/authorize"
            element={<ProtectedRoute element={<OpenTable />} />}
          />
        </Route>

        {/* Hotels (IA §2): a sibling layout, never inside AppLayout, whatever
            the frame flag says. Not found unless the store group has a
            property; platform admins pick the group. */}
        <Route element={<HotelShell />}>
          <Route
            path="/hotel/*"
            element={
              <ProtectedRoute
                element={
                  <Suspense fallback={null}>
                    <HotelApp />
                  </Suspense>
                }
              />
            }
          />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  /* Guests are not staff: no AuthProvider, AppLayout or ProtectedRoute. */
  {
    path: '/p/*',
    element: (
      <Suspense fallback={null}>
        <GuestPortal />
      </Suspense>
    ),
  },
  {
    path: '*',
    element: <AppRoutes />,
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
